import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { bins, estimateTotal, formatWeekRange, messages } from '@fruitways/shared';
import { Button, ConfirmDialog, EmptyState, ListRow, SkeletonRows } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import { useCapture } from '@/capture/CaptureContext';
import { deleteEstimate } from '@/db/estimateActions';
import { useEstimate, useMasterData } from '@/hooks/useMasterData';
import { formatDateTime } from '@/lib/format';
import { growerOf, useGrowers, useIsAdmin } from '@/hooks/useAdmin';
import { statusLabel } from '@/screens/Dashboard/EstimateRow';
import { EstimateTable } from '@/screens/Capture/EstimateTable';
import '@/screens/Capture/capture.css';

/** A stored estimate: facts, the day-by-variety table, the total, and Edit / Delete. */
export function DetailScreen() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const master = useMasterData();
  const estimate = useEstimate(id);
  const { startEdit } = useCapture();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const isAdmin = useIsAdmin();
  const growers = useGrowers();

  const farm = master?.farms.find((f) => f.id === estimate?.farmId);
  const season = master?.seasons.find((s) => s.id === estimate?.seasonId);
  const back = () => nav(from === '/estimates' ? '/estimates' : '/');

  async function remove() {
    if (!estimate) return;
    setBusy(true);
    try {
      await deleteEstimate(estimate.id);
      nav('/capture/success', {
        replace: true,
        state: { outcome: 'deleted', farmName: farm?.name ?? '', weekNumber: estimate.weekNumber },
      });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  }

  return (
    <AppFrame
      title={estimate ? messages.common.weekN(estimate.weekNumber) : messages.detail.fallbackTitle}
      subtitle={
        estimate && farm ? messages.header.farmWeek(farm.name, estimate.weekNumber) : undefined
      }
      onBack={back}
      action={<ProfileMenu />}
    >
      <div className="fw-content" style={{ paddingBottom: 'var(--space-4)' }}>
        {estimate === undefined && <SkeletonRows count={3} />}
        {estimate === null && <EmptyState icon="leaf-outline" title={messages.errors.notFound} />}
        {estimate && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
              }}
            >
              <span className="fw-secondary fw-tnum">{formatWeekRange(estimate.weekStart)}</span>
              {statusLabel(estimate.status)}
            </div>

            <div className="fw-rows">
              <ListRow title={messages.common.season} meta={season?.label ?? ''} />
              {isAdmin && (
                <ListRow
                  title={messages.admin.filter.grower}
                  meta={growerOf(growers, estimate.farmId)?.displayName ?? ''}
                />
              )}
              <ListRow title={messages.common.productionUnit} meta={farm?.name ?? ''} />
              <ListRow
                title={messages.detail.submitted}
                meta={formatDateTime(estimate.submittedAt)}
              />
            </div>

            <EstimateTable lines={estimate.lines} />

            <div
              className="fw-panel fw-panel--sunken"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span className="fw-label">{messages.detail.totalSubmitted}</span>
              <span className="fw-title fw-tnum">{bins(estimateTotal(estimate))}</span>
            </div>
          </>
        )}
      </div>

      {estimate && (
        <div className="fw-actions">
          <Button
            variant="secondary"
            icon="create-outline"
            onClick={() => {
              startEdit(estimate);
              nav('/capture/entry');
            }}
          >
            {messages.common.edit}
          </Button>
          <Button variant="danger" icon="trash-outline" onClick={() => setConfirm(true)}>
            {messages.common.delete}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirm}
        title={messages.dialogs.deleteEstimate.title}
        body={
          estimate
            ? messages.dialogs.deleteEstimate.body(farm?.name ?? '', estimate.weekNumber)
            : ''
        }
        confirmLabel={messages.dialogs.deleteEstimate.confirm}
        cancelLabel={messages.common.cancel}
        variant="danger"
        busy={busy}
        onCancel={() => setConfirm(false)}
        onConfirm={remove}
      />
    </AppFrame>
  );
}
